import { debounce } from "./../utils/debounce";

// Models
interface ICart {
    CartItems: ICartItem[]
    SubTotal?: number
}

interface ICartItem {
    ProductId: string
    ProductName: string
    ProductNumber: string
    ProductType: string
    Quantity: number
    CurrentUserPrice: {
        IsMonthly: boolean
        Amount: number
        CurrencyCode: string
        CurrencySymbol: string
    }
    VatName: string
    VatPercentage: number
    VatCode: string
    ProductMetadatas: {
        DisplayName: string
        ImageUrl: string
    }[]
    ProductVariantCategories?: {
        Order: number
        Name: string
        Value: string
    }[]
}

// Cart function
const initializeCart = function () {
    // DOM References
    const $headerCartButton = $('.header-cart__cta[toggle-class-target]');
    const $headerCartModal = $('#headerCartModal');
    const $headerCartBody = $headerCartModal.find('.header-cart__modal__body');
    const $headerCartBodyCart = $headerCartBody.find('.cart-items');
    const $headerCartFooterSubTotalContainer = $headerCartModal.find('.header-cart__modal__footer--subtotal__value');
    const $headerCartFooterSubTotal = $headerCartFooterSubTotalContainer.find('.subtotal__value');
    const $headerCartFooterSubTotalVat = $headerCartFooterSubTotalContainer.find('.subtotal__vat');
    const $headerCartFooterShippingFree = $headerCartModal.find('#headerCartModal_EligibleFreeShipping');

    // Constants
    const loadingClass = 'loading';
    const cartNotEmptyClass = 'not-empty';

    // APIs
    const headers: { [key: string]: string } = {};
    const onGoingXMLHttpRequests: { [key: string]: XMLHttpRequest } = {};

    (function () {
        if ($.ServicesFramework) {
            // Set up services framework.
            const sf = $.ServicesFramework('-1');
            headers['ModuleId'] = `${sf.getModuleId()}`;

            // Set the anti-forgery token header.
            const antiForgeryKey = sf.getAntiForgeryKey();
            const antiForgeryValue = sf.getAntiForgeryValue();
            headers[antiForgeryKey] = antiForgeryValue;

            // Fallback in case getAntiForgeryKey() does not return the correct key.
            if (antiForgeryKey !== 'RequestVerificationToken') {
                headers['RequestVerificationToken'] = antiForgeryValue;
            }

            Object.freeze(headers);
        }
    })();

    const getCart = function (callback: (cart: ICart) => void): XMLHttpRequest {
        let request = new XMLHttpRequest();
        request.open('GET', '/DesktopModules/RhaSilverbearShop/API/CartApi/GetCart?ignoreCache=true', true);

        Object.keys(headers).forEach(function (headerKey) {
            request.setRequestHeader(headerKey, headers[headerKey]);
        });

        request.onreadystatechange = function () {
            let cart: ICart = { CartItems: [], SubTotal: 0 };

            var status = request.status;
            if (status > 0) {
                if (request.readyState === XMLHttpRequest.DONE) {
                    if (status >= 200 && status < 400 && request.responseText) {
                        cart = JSON.parse(request.responseText);
                    } else {
                        console.error('An error has occurred while retrieveing products for the cart.');
                    }
                }

                callback && (typeof callback === 'function') && callback(cart);
            }
        };

        request.send();

        return request;
    }

    const updateCartItem = function (productId: string, quantity: number, callback: (productId: string, quantity: number) => void): XMLHttpRequest {
        if (!productId) {
            callback && (typeof callback === 'function') && callback(productId, quantity);
            return;
        }

        if (quantity < 0) {
            quantity = 0;
        }

        let request = new XMLHttpRequest();
        request.open('GET', `/Design-Guide?wsproductid=${productId}&wsquantity=${quantity}&wsamendproduct=1`, true);

        Object.keys(headers).forEach(function (headerKey) {
            request.setRequestHeader(headerKey, headers[headerKey]);
        });

        request.onreadystatechange = function () {
            var status = request.status;
            if (status > 0) {
                if (request.readyState === XMLHttpRequest.DONE) {
                    var status = request.status;

                    if (!(status >= 200 && status < 400)) {
                        console.error('An error has occurred while updating cart.');
                    }
                }

                callback && (typeof callback === 'function') && callback(productId, quantity);
            }
        };

        request.send();
        return request;
    }

    const deleteCartItem = function (productId: string, callback: (productId: string, quantity: number) => void): XMLHttpRequest {
        return updateCartItem(productId, 0, callback);
    }

    // Functions
    const _showLoadingSpinner = function ($element: JQuery<HTMLElement>, shouldDisplay: boolean) {
        if (shouldDisplay) {
            if (!$element.hasClass(loadingClass)) {
                $element.addClass(loadingClass);
            }
        }
        else {
            if ($element.hasClass(loadingClass)) {
                $element.removeClass(loadingClass);
            }
        }
    }

    const showHeaderCartBodyCartSpinner = function (shouldDisplay: boolean) {
        _showLoadingSpinner($headerCartBody, shouldDisplay);
    }

    const updateStateOfCartIfNotEmpty = function (isCartEmpty: boolean) {
        if (!isCartEmpty) {
            if (!$headerCartButton.hasClass(cartNotEmptyClass)) {
                $headerCartButton.addClass(cartNotEmptyClass);
            }
        }
        else if ($headerCartButton.hasClass(cartNotEmptyClass)) {
            $headerCartButton.removeClass(cartNotEmptyClass);
        }
    }

    const isCartItemVatFree = (cartItem: ICartItem) => {
        switch (cartItem.VatCode) {
            case 'Z':
            case 'E':
                return true;

            default:
                return false;
        }
    }

    // Renderers
    const renderCartSubTotal = function (cart: ICart) {
        const subTotal = cart?.SubTotal || 0;
        $headerCartFooterSubTotal.text(`£${subTotal.toFixed(2)}`);

        if (!cart?.CartItems.length) {
            
            $headerCartFooterSubTotalVat.html('');
        }
        else if (cart.CartItems.every(isCartItemVatFree)) {
            $headerCartFooterSubTotalVat.html('\u00A0VAT Exempt');
        }
        else {
            $headerCartFooterSubTotalVat.html('<span class="small"><span class="small font-weight-bold"> +VAT</span></span>');
        }
    }

    const renderShippingInformation = function (subTotal: number) {
        const freeShippingTextTemplate = $headerCartFooterShippingFree.attr('data-text');
        const freeShippingMinThreshold = parseFloat($headerCartFooterShippingFree.attr('data-eligiblefreeshippingprice'));

        const freeShippingERV = freeShippingMinThreshold - subTotal;

        const freeShippingText = freeShippingERV > 0
            ? freeShippingTextTemplate.replace('{{EligibleFreeShippingPrice}}', '£' + freeShippingERV.toFixed(2))
            : 'You are eligible for free shipping.';

        $headerCartFooterShippingFree.text(freeShippingText)
    }

    type OnGoingCartItemXMLHttpRequestInfo = {
        [key: string]: {
            quantity: number
        }
    };

    const onGoingUpdateCartItemXMLHttpRequestsInfo: OnGoingCartItemXMLHttpRequestInfo = {};

    function abortOnGoingUpdateCartItemXMLHttpRequestOtherThan(xmlHttpRequestKey: string) {
        Object.keys(onGoingXMLHttpRequests).forEach(function (onGoingXMLHttpRequestKey: string) {
            if (onGoingXMLHttpRequestKey !== xmlHttpRequestKey) {
                if (onGoingXMLHttpRequests[onGoingXMLHttpRequestKey].readyState !== XMLHttpRequest.DONE) {
                    onGoingXMLHttpRequests[onGoingXMLHttpRequestKey].abort();
                }

                delete onGoingXMLHttpRequests[onGoingXMLHttpRequestKey];
            }
        });
    }

    function setValueForCartItemQuantityController(cartItem: ICartItem, quantity: number) {
        const $cartItemInfoPrice = $headerCartBodyCart.find(`.cart-item[data-product-id="${cartItem.ProductId}"] .cart-item__info__price-delete__price`);
        const $cartItemInfoPriceValue = $cartItemInfoPrice.find('.cart-item__info__price-delete__price-value');

        const getCartXMLHttpRequestKey = `updateCartItem-${cartItem.ProductId}-getCart-${quantity}`;

        onGoingUpdateCartItemXMLHttpRequestsInfo[cartItem.ProductId] = { quantity };
        abortOnGoingUpdateCartItemXMLHttpRequestOtherThan(getCartXMLHttpRequestKey);

        // Show spinners for elements that are changing.
        _showLoadingSpinner($cartItemInfoPrice, true);
        _showLoadingSpinner($headerCartFooterSubTotalContainer, true);

        updateCartItem(cartItem.ProductId, quantity, function (productId, qty) {
            abortOnGoingUpdateCartItemXMLHttpRequestOtherThan(`updateCartItem-${productId}-getCart-${qty}`);

            if (qty < 1) {
                renderCart();
                _showLoadingSpinner($headerCartFooterSubTotalContainer, false);
            }
            else {
                if (qty === onGoingUpdateCartItemXMLHttpRequestsInfo[productId].quantity) {
                    onGoingXMLHttpRequests[`updateCartItem-${productId}-getCart-${qty}`] = getCart(function (cart) {
                        if (qty === onGoingUpdateCartItemXMLHttpRequestsInfo[productId].quantity) {
                            const updatedCartItems = (cart?.CartItems || []).filter(function (item) {
                                return item.ProductId === productId;
                            });

                            if (updatedCartItems.length) {
                                const updatedCartItem = updatedCartItems[0];

                                // - recompute item price
                                $cartItemInfoPriceValue.text(`${updatedCartItem.CurrentUserPrice.CurrencySymbol}${(updatedCartItem.CurrentUserPrice.Amount * updatedCartItem.Quantity).toFixed(2)} `);

                                // - recompute total price
                                renderCartSubTotal(cart);

                                // - recompute eligibility for free shipping
                                renderShippingInformation(cart?.SubTotal || 0);
                            }

                            // Hide spinners
                            _showLoadingSpinner($cartItemInfoPrice, false);
                            _showLoadingSpinner($headerCartFooterSubTotalContainer, false);
                        }
                    });
                }
            }
        });
    }

    function createCartItemQuantityController(cartItem: ICartItem) {
        const $quantityContainer = $('<div class="cart-item__info__detail--qty"></div>');

        // Quantity Label
        const $quantityLabel = $('<div class="cart-item__info__detail--qty__label">Qty: </div>');
        $quantityContainer.append($quantityLabel);

        // Quantity Controller
        const $quantityController = $('<div class="cart-item__info__detail--qty__ctrl"></div>');
        const $quantityControllerTextbox = $(`<input id=${cartItem.ProductId} class="cart-item__info__detail--qty__ctrl-tbox" type="number" min="0" value="${cartItem.Quantity}" />`);

        // Adding change listener works only on rendered DOM elements already.
        $headerCartBodyCart.on('change', `input#${cartItem.ProductId}`, debounce(function () {
            setValueForCartItemQuantityController(cartItem, parseInt($(this).val() as string));
        }, 150, false) as any);

        const $quantityControllerDecrementerBtn = $('<button type="button" class="btn cart-item__info__detail--qty__ctrl-dec"><i class="fas fa-minus" aria-hidden="true"></i></div>');
        $quantityControllerDecrementerBtn.on('click', function () {
            $quantityControllerTextbox.val(parseInt($quantityControllerTextbox.val() as string) - 1);
            $quantityControllerTextbox.trigger("change");
        });

        const $quantityControllerIncrementerBtn = $('<button type="button" class="btn cart-item__info__detail--qty__ctrl-inc"><i class="fas fa-plus" aria-hidden="true"></i></div>');
        $quantityControllerIncrementerBtn.on('click', function () {
            $quantityControllerTextbox.val(parseInt($quantityControllerTextbox.val() as string) + 1);
            $quantityControllerTextbox.trigger("change");
        });

        $quantityController.append($quantityControllerDecrementerBtn);
        $quantityController.append($quantityControllerTextbox);
        $quantityController.append($quantityControllerIncrementerBtn);
        $quantityContainer.append($quantityController);

        return $quantityContainer;
    }

    function renderCartItem(cartItem: ICartItem) {
        const $cartItemContainer = $(`<div class="cart-item" data-product-id="${cartItem.ProductId}"></div>`);

        // Create Image Container
        const $cartItemImageContainer = $('<div class="cart-item__img-container"></div>');

        // Create Image
        const $cartItemImage = $('<img class="cart-item__img">');

        if (cartItem.ProductMetadatas.length && cartItem.ProductMetadatas[0].ImageUrl) {
            $cartItemImage.attr('src', cartItem.ProductMetadatas[0].ImageUrl);
            $cartItemImage.attr('alt', cartItem.ProductMetadatas[0].DisplayName);
        }
        else {
            $cartItemImage.attr('src', "/DesktopModules/Cantarus/RhaSilverbearShop/Clients/assets/awaiting_product_image.png");
            $cartItemImage.attr('alt', cartItem.ProductName);
        }

        // Append Image & Container
        $cartItemImageContainer.append($cartItemImage);
        $cartItemContainer.append($cartItemImageContainer);

        // Create Info Container
        const $cartItemInfoContainer = $('<div class="cart-item__info-container"></div>');

        // ROWS

        // ROW 1 - NAME
        // Create Name
        const $cartItemInfoName = $('<div class="cart-item__info__name"></div>');
        let $cartItemInfoNameLink;

        if (cartItem.ProductType === 'Membership' || cartItem.ProductType === 'Subscription') {
            $cartItemInfoNameLink = $(`<span></span>`);
        }
        else {
            $cartItemInfoNameLink = $(`<a href="/shop/product/${cartItem.ProductNumber}"></a>`);
        }

        $cartItemInfoNameLink.text(cartItem.ProductName);
        $cartItemInfoName.append($cartItemInfoNameLink);
        $cartItemInfoContainer.append($cartItemInfoName);

        // ROW 2 - PRODUCT NUMBER
        // Create Code Container
        const $cartItemInfoCode = $('<div class="cart-item__info__code"></div>');
        $cartItemInfoCode.text(`Product Code: ${cartItem.ProductNumber}`);
        $cartItemInfoContainer.append($cartItemInfoCode);

        // ROW 3 - VARIANCES
        // Create Cart item detail row for Variances
        if (cartItem.ProductVariantCategories?.length) {
            const $cartItemInfoDetailVariances = $('<div class="cart-item__info__detail"></div>');
            (cartItem.ProductVariantCategories || []).forEach(function (variantCategory) {
                const $cartItemVariance = $('<div class="cart-item__info__detail--variance"></div>');
                $cartItemVariance.text(`${variantCategory.Name}: ${variantCategory.Value}`);
                $cartItemInfoDetailVariances.append($cartItemVariance);
            });
            $cartItemInfoContainer.append($cartItemInfoDetailVariances);
        }

        // ROW 4 - QUANTITY
        // Create Cart Item detail row for Quantity
        const $cartItemInfoDetailQuantity = $('<div class="cart-item__info__detail"></div>');
        const $cartItemQuantityController = createCartItemQuantityController(cartItem);
        $cartItemInfoDetailQuantity.append($cartItemQuantityController);
        $cartItemInfoContainer.append($cartItemInfoDetailQuantity);

        // ROW 5 - PRICE + Delete
        // Create Price + Delete Container
        const $cartItemInfoPriceDelete = $('<div class="cart-item__info__price-delete">')

        // Create Price
        const $cartItemInfoPrice = $('<div class="cart-item__info__price-delete__price"></div>');
        const $cartItemInfoPriceSpinner = $('<div class="dnnPanelLoading"></div>')
        $cartItemInfoPrice.append($cartItemInfoPriceSpinner);

        const $cartItemInfoPriceValue = $('<span class="cart-item__info__price-delete__price-value"></span>')
        $cartItemInfoPriceValue.text(`${cartItem.CurrentUserPrice.CurrencySymbol}${(cartItem.CurrentUserPrice.Amount * cartItem.Quantity).toFixed(2)} `);
        $cartItemInfoPrice.append($cartItemInfoPriceValue);

        if (!isCartItemVatFree(cartItem)) {
            const $cartItemInfoPriceVAT = $('<span class="small"></span>');
            $cartItemInfoPriceVAT.html('<span class="small font-weight-bold">+VAT</span>');
            $cartItemInfoPrice.append($cartItemInfoPriceVAT);
        }
        else {
            const $cartItemInfoPriceVAT = $('<span class="small"></span>');
            $cartItemInfoPriceVAT.html('<span class="small font-weight-bold">\u00A0VAT Exempt</span>');
            $cartItemInfoPrice.append($cartItemInfoPriceVAT);
        }

        if (cartItem.CurrentUserPrice.IsMonthly) {
            const $cartItemInfoPriceDD = $('<span class="small"></span>');
            $cartItemInfoPriceDD.html('<span class="small font-weight-bold"> / month</span>');
            $cartItemInfoPrice.append($cartItemInfoPriceDD);
        }

        $cartItemInfoPriceDelete.append($cartItemInfoPrice);

        // Create Delete button
        const $cartItemDeleteCta = $('<button class="cart-item__info__price-delete__delete" type="button"><span class="fa fa-trash-alt" aria-hidden="true"></span></button>');
        $cartItemDeleteCta.on('click', function () {
            showHeaderCartBodyCartSpinner(true);

            deleteCartItem(cartItem.ProductId, function (_productId, _quantity) {
                renderCart();
            });
        });
        $cartItemInfoPriceDelete.append($cartItemDeleteCta);

        // Append Price + Delete Container to ItemInfoContainer
        $cartItemInfoContainer.append($cartItemInfoPriceDelete);

        // END OF ROWS

        // Append Item Container
        $cartItemContainer.append($cartItemInfoContainer);
        $headerCartBodyCart.append($cartItemContainer);
    }

    function renderCart() {
        showHeaderCartBodyCartSpinner(true);

        getCart(function (cart) {
            // Ensure that no products are displayed
            $headerCartBodyCart.html('');

            // Render basket not empty
            updateStateOfCartIfNotEmpty(!cart?.CartItems || cart.CartItems.length === 0);

            // Render each cartItem
            if (cart?.CartItems.length) {
                cart.CartItems.forEach(function (cartItem) {
                    renderCartItem(cartItem);
                });
            }
            else {
                $headerCartBodyCart.html('No products in the cart!');
            }

            // Render subtotal
            renderCartSubTotal(cart);

            // Render shipping information
            renderShippingInformation(cart?.SubTotal || 0);

            showHeaderCartBodyCartSpinner(false);
        });
    }

    // Listeners
    document.addEventListener("RHA:FetchLatestCart", function (_) {
        renderCart();
    });

    window.addEventListener('click', function (e) {
        if ($headerCartModal && !$headerCartModal.hasClass('d-none')) {
            if ($headerCartModal.get(0).contains(e.target as HTMLElement)) {
                // Clicked inside the cart modal
                return;
            } else if (!$headerCartButton.get(0).contains(e.target as HTMLElement)) {
                // Clicked outside the cart modal
                $headerCartButton.trigger('click');
            }
        }
    });

    // Init
    renderCart();
}

$(initializeCart);
